import {
    DEFAULT_PAGE_ID,
    PAGE_SIDEBAR_MAP,
} from '@/constants/catalog';
import router from '@/router';
import { onCefEvent } from '@/utils/cefEvents';
import { track } from '@/utils/track';
import WinContext from '@/utils/WinContext';
import * as Api from "@/request/api/defaultApi";

const PAGE_IDS = new Set(Object.keys(PAGE_SIDEBAR_MAP));

// 窗口尺寸变化事件：事件名以 c 端为准（client 是 c 端的拼写）
const WINDOW_SIZE_EVENTS = ['client_size_change']; 
// c 端传的是 Windows 的 WM_SIZE 常量：0 = SIZE_RESTORED（普通大小），2 = SIZE_MAXIMIZED（最大化）
const SIZE_MAXIMIZED = 2;
// 窗口状态只订阅一次
let windowStateListenerRegistered = false;

function resolvePageId(pageId) {
    if (typeof pageId === 'string' && PAGE_IDS.has(pageId)) {
        return pageId;
    }

    return DEFAULT_PAGE_ID;
}

export const useAppStore = defineStore('AppStore', {
    state: () => ({
        // 全局window对象
        MyWin: null,
        // 窗口是否处于最大化（由 C++ 的 client_size_change 事件同步，控制标题栏最大化/恢复图标）
        isMaxSize: false,
        // 页面切换与导航高亮状态。
        currentPage: 'page-home',
        activeSidebarSection: 'home',

        // 登录弹层与当前用户信息。
        avatarMenuVisible: false,
        loginModalVisible: false,
        loginView: 'login',

        // 登录相关信息
        loginQRCode: new URL("@/assets/global/qrcode_load.png", import.meta.url).href,
        loginInTimer: null,
        loginInTimeOut: 300000,
        isLoginReset: false,
        UserInfo: null,

        // 全局提示状态。
        toastVisible: false,
        toastMessage: '操作成功',
        toastTimer: null,

        // 请求c++获取的渠道配置信息
        ClientInfo: null,

        // 启动时拉到的云控配置（/workai/index/config 的 data）：域名、下载包列表、客服地址等
        AppConfig: null,

        // 是否展示全部功能（由 C++ client_get_show_all_state 下发）。
        // 为 false 时侧边栏隐藏「办公软件」「Ai插件广场」「已安装插件」。
        // 默认 false：状态未知一律按展示默认处理，避免拉取完成前先渲染出受限入口再收回。
        showAllFeatures: false,
    }),

    getters: {
        // 用户头像
        avatarLabel(state) {
            if (!state.UserInfo?.nickname) {
                return '微';
            }
            return state.UserInfo?.nickname.charAt(0);
        },
    },

    actions: {
        // 修改全局window对象
        SET_MyWin() {
            // 客户端初始化
            this.MyWin = new WinContext();
            console.log('appStore=init初始化==>', this.MyWin);
        },
        SET_ClientInfo(data) {
            this.ClientInfo = data;
        },
        // 监听窗口最大化/还原。状态以 C++ 推送为准，不再靠"点了几次按钮"自己猜——
        // 拖拽边界、双击标题栏、Win+↑ 这些绕开按钮的操作同样会推事件，图标才不会错位。
        registerWindowStateListener() {
            if (windowStateListenerRegistered) return;
            windowStateListenerRegistered = true;

            onCefEvent((msgType: any, msg: any) => {
                if (!WINDOW_SIZE_EVENTS.includes(msgType)) return;

                // msg 可能是数字、字符串，也可能被包了一层对象
                const raw = msg !== null && typeof msg === 'object' ? (msg.data ?? msg.value ?? msg.size) : msg;
                const state = Number(raw);

                if (Number.isNaN(state)) {
                    console.warn('[窗口状态] 无法识别的 client_size_change 值', msg);
                    return;
                }

                this.isMaxSize = state === SIZE_MAXIMIZED;
            });
        },
        // 拉云控配置并写入 AppConfig，返回本次拿到的配置（失败返回 null）。
        // 配置来源统一收在这里：启动引导、安装流程都走它，避免各处重复拼参数。
        // bootstrap=true 用于启动阶段，超时更短（所有请求都在等它下发域名）。
        async FETCH_AppConfig(options: { bootstrap?: boolean } = {}) {
            const params = {
                client_version: this.ClientInfo?.client_version,
                channel: this.ClientInfo?.client_channel,
                uuid: this.ClientInfo?.client_guid,
                api_version: "1.0"
            };

            try {
                const res: any = options.bootstrap
                    ? await Api.getBootstrapConfig(params)
                    : await Api.getConfig(params);

                if (res?.code == 200) {
                    this.AppConfig = res.data;
                    return res.data;
                }

                console.warn("[云控配置] 返回异常", res);
            } catch (err) {
                console.error("[云控配置] 拉取失败", err);
            }

            return null;
        },
        // 路由与侧边栏高亮解耦，便于保留“支持”这类非跳转项的交互状态。
        setSidebarActive(section) {
            this.activeSidebarSection = section;
        },
        // 同步路由到当前页面和侧边栏状态
        syncRoute(pageId) {
            const nextPage = resolvePageId(pageId);
            this.currentPage = nextPage;
            this.activeSidebarSection = PAGE_SIDEBAR_MAP[nextPage] || 'home';
        },
        // 跳转页面
        goPage(pageId) {
            const nextPage = resolvePageId(pageId);
            void router.push({ name: nextPage });
        },
        // 跳转到主页
        goHome() {
            this.goPage(DEFAULT_PAGE_ID);
        },
        // 修改用户信息
        async SET_UserInfo(data) {
            this.UserInfo = data;

            const PluginsMineStore = usePluginsMineStore();
            await this.MyWin.WorkAiApi("client_save_data", this.strToBase64(JSON.stringify({
                UserInfo: data,
                installedPlugins: PluginsMineStore.installedPlugins
            })));
        },
        // 打开登录弹窗
        async openLogin() {
            this.loginView = 'login';
            this.loginModalVisible = true;
            let result: any = await Api.LoginIn();
            if (result.code == 200) {
                this.loginQRCode = result.data.url;

                this.loginInTimer = setInterval(async () => {
                    let res: any = await Api.getLoginStatus({ scene_id: result.data.scene_id, channel: this.ClientInfo.client_channel });
                    if (res.code == 0) {
                        this.SET_UserInfo({ ...res.data, nickname: "微信用户" });

                        clearInterval(this.loginInTimer);
                        this.loginInTimer = null;

                        // 登录成功埋点（不 await：上报失败或变慢不能挡住下面切换成功态、关闭弹窗）
                        track("loginsuccess");

                        setTimeout(() => {
                            this.loginView = 'success';

                            setTimeout(() => {
                                this.closeLogin();
                            }, 1200);
                        }, 1000);
                    }
                }, 1000)

                setTimeout(() => {
                    clearInterval(this.loginInTimer);
                    this.loginInTimer = null;
                    this.SET_isLoginReset(true);
                }, this.loginInTimeOut)
            }
        },
        // 点击刷新登录二维码
        SET_isLoginReset(data) {
            this.isLoginReset = data;
        },
        // 关闭登录弹层时清空挂起动作
        closeLogin() {
            this.loginModalVisible = false;
            clearInterval(this.loginInTimer);
            this.loginInTimer = null;
        },

        // 切换账号弹窗状态
        toggleAvatarMenu() {
            this.avatarMenuVisible = !this.avatarMenuVisible;
        },
        // 关闭账号弹窗
        closeAvatarMenu() {
            this.avatarMenuVisible = false;
        },
        // 退出登录
        doLogout() {
            this.closeAvatarMenu();
            this.clearState();
            this.goHome();
            this.toast('👋 已退出登录');
        },
        // 清空本地存储状态
        async clearState() {
            const PluginsMineStore = usePluginsMineStore();

            this.UserInfo = null;
            // 清空c++端用户信息
            await this.MyWin.WorkAiApi("client_save_data", this.strToBase64(JSON.stringify({
                installedPlugins: PluginsMineStore.installedPlugins
            })));

            // PluginsMineStore.SET_installedPlugins([]);
        },
        // 拉取「是否展示全部功能」状态（页面加载后调用），决定侧边栏展示全部还是默认。
        async FETCH_ShowAllState() {
            // 兜底为 false：调用失败或返回异常时一律按「展示默认」处理
            let show = false;
            try {
                const res: any = await this.MyWin.WorkAiApi("client_get_show_all_state", "");
                console.log("[是否展示全部功能] client_get_show_all_state =>", res);

                if (res?.code == 200 && res?.data) {
                    show = res.data.show === true;
                } else {
                    console.warn("[是否展示全部功能] 返回异常，按展示默认处理", res);
                }
            } catch (err) {
                console.warn("[是否展示全部功能] 获取状态失败，按展示默认处理", err);
            }

            this.showAllFeatures = show;
        },
        // 统一控制轻提示展示时长与显隐状态。
        toast(message) {
            this.toastMessage = message;
            this.toastVisible = true;
            clearTimeout(this.toastTimer);
            this.toastTimer = setTimeout(() => {
                this.toastVisible = false;
            }, 2800);
        },
        // 字符串 → base64
        strToBase64(str) {
            const bytes = new TextEncoder().encode(str);          // UTF-8 字节
            const binary = Array.from(bytes, b => String.fromCharCode(b)).join("");
            return btoa(binary);
        },
        // base64 → 字符串
        base64ToStr(b64) {
            const binary = atob(b64);
            const bytes = Uint8Array.from(binary, c => c.charCodeAt(0));
            return new TextDecoder().decode(bytes);
        }
    }
});
